import { useState, useRef, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router';
import { usePrivy } from '@privy-io/react-auth';
import { useQueryClient } from '@tanstack/react-query';
import { Send, AlertTriangle, ArrowLeft, Sparkles, Loader2, Info } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/app/components/ui/dialog';
import { useAuthStore } from '@/stores/authStore';
import { useStartChat, useSendMessage, useChat, QUERY_KEYS } from '@/api';
import { TypewriterText } from '@/app/components/chat/TypewriterText';
import type { ChatMessage } from '@/api';

export function ChatPage() {
  const { botId } = useParams<{ botId: string }>();
  const navigate = useNavigate();
  const { authenticated: isConnected } = usePrivy();
  const { user, updateMessageBalance } = useAuthStore();
  const messageBalance = user?.remainingMessages ?? 0;

  const [chatId, setChatId] = useState<string | null>(null);
  const [inputText, setInputText] = useState('');
  const [optimisticUserMessage, setOptimisticUserMessage] = useState<string | null>(null);
  const [pendingAiResponse, setPendingAiResponse] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();

  const startChat = useStartChat();
  const sendMessage = useSendMessage();
  const { data: chatData, isLoading: isLoadingChat } = useChat(chatId ?? undefined, !!chatId);

  const character = chatData?.data?.character;
  const rawMessages: ChatMessage[] = chatData?.data?.messages ?? [];
  // When showing typewriter, hide the last 2 from server only if they match (refetch completed)
  // to avoid duplicate user+AI while transitioning
  const messages =
    pendingAiResponse &&
    rawMessages.length >= 2 &&
    rawMessages[rawMessages.length - 1]?.sender === 'model' &&
    rawMessages[rawMessages.length - 1]?.content === pendingAiResponse
      ? rawMessages.slice(0, -2)
      : rawMessages;

  // Start or resume chat when we have characterId
  useEffect(() => {
    if (!botId || !isConnected) return;

    const initChat = async () => {
      try {
        const res = await startChat.mutateAsync({ characterId: botId });
        if (res.data?.chatId) {
          setChatId(res.data.chatId);
        }
      } catch (err) {
        console.error('Failed to start chat:', err);
        navigate('/explore');
      }
    };

    if (!startChat.isPending && !chatId) {
      initChat();
    }
  }, [botId, isConnected]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, optimisticUserMessage, pendingAiResponse]);

  // Keep scrolling during typewriter; use instant scroll + throttle to avoid jank
  useEffect(() => {
    if (!pendingAiResponse) return;
    const scrollInstant = () => messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
    const interval = setInterval(scrollInstant, 150);
    return () => clearInterval(interval);
  }, [pendingAiResponse]);

  const handleSend = async () => {
    if (!inputText.trim() || !isConnected || !chatId || messageBalance < 1) return;

    const content = inputText.trim();
    setInputText('');
    setOptimisticUserMessage(content);

    try {
      const res = await sendMessage.mutateAsync({ chatId, content });
      if (res.data?.remainingMessages !== undefined) {
        updateMessageBalance(res.data.remainingMessages);
      }
      setPendingAiResponse(res.data?.aiResponse ?? null);
    } catch (err) {
      console.error('Failed to send message:', err);
      setInputText(content);
      setOptimisticUserMessage(null);
    }
  };

  const handleTypewriterComplete = async () => {
    if (!chatId) return;
    // Refetch first, then clear local state — avoids flash when switching to server data
    await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.CHAT.DETAIL(chatId) });
    setOptimisticUserMessage(null);
    setPendingAiResponse(null);
  };

  const characterCount = inputText.length;
  const maxCharacters = 500;
  const isLoading = startChat.isPending || (!!chatId && isLoadingChat);
  const isSending = sendMessage.isPending;

  if (!isConnected) {
    return (
      <div className="h-[calc(100dvh-73px)] sm:h-[calc(100vh-73px)] flex flex-col items-center justify-center gap-4 px-4">
        <p className="text-[#9595a5]">Connect your wallet to chat</p>
        <Link to="/explore">
          <Button className="bg-white hover:opacity-90">Go to Explore</Button>
        </Link>
      </div>
    );
  }

  if (!botId) {
    navigate('/explore');
    return null;
  }

  if (isLoading && !character) {
    return (
      <div className="h-[calc(100dvh-73px)] sm:h-[calc(100vh-73px)] flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-white animate-spin" />
      </div>
    );
  }

  return (
    <div className="h-[calc(100dvh-73px)] sm:h-[calc(100vh-73px)] flex flex-col min-h-0">
      {/* Chat Header - sticky on mobile */}
      <div className="sticky top-0 z-20 shrink-0 border-b border-white/[0.08] bg-[#18181f]/95 backdrop-blur-xl px-4 py-3 sm:px-6 sm:py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between gap-2 min-w-0">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0 flex-1">
            <Link to="/explore" className="shrink-0">
              <button className="p-2 rounded-lg hover:bg-[#2a2a35] transition-colors">
                <ArrowLeft className="w-5 h-5 text-[#9595a5]" />
              </button>
            </Link>
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-[#2a2a35] flex-shrink-0 flex items-center justify-center overflow-hidden">
              {character?.imageUrl ? (
                <img src={character.imageUrl} alt={character.name} className="w-full h-full object-cover" />
              ) : (
                <span className="text-xl sm:text-2xl">🤖</span>
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5 sm:block">
                <h2 className="font-['Poppins',sans-serif] text-base sm:text-lg font-semibold text-white truncate">
                  {character?.name ?? 'Loading...'}
                </h2>
                {/* Mobile: Info button for description */}
                <Dialog>
                  <DialogTrigger asChild>
                    <button
                      type="button"
                      className="sm:hidden shrink-0 p-1.5 rounded-lg hover:bg-[#2a2a35] transition-colors text-[#9595a5] hover:text-white"
                      aria-label="View character info"
                    >
                      <Info className="w-4 h-4" />
                    </button>
                  </DialogTrigger>
                  <DialogContent className="bg-[#18181f] border-white/[0.08] text-left max-w-[calc(100vw-2rem)]">
                    <DialogHeader>
                      <DialogTitle className="text-white font-['Poppins',sans-serif]">
                        {character?.name}
                      </DialogTitle>
                      <p className="text-sm text-[#9595a5] mt-1 whitespace-pre-wrap">
                        {character?.description ?? 'No description available.'}
                      </p>
                    </DialogHeader>
                  </DialogContent>
                </Dialog>
              </div>
              {/* Description: desktop only */}
              <p className="hidden sm:block text-sm text-[#9595a5] line-clamp-1 mt-0.5">
                {character?.description ?? ''}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            <div className="px-2.5 py-1.5 sm:px-4 sm:py-2 rounded-lg bg-[#2a2a35]/50 border border-white/[0.08]">
              <span className="text-sm text-white font-medium">{messageBalance}</span>
              <span className="hidden sm:inline text-xs text-[#9595a5] ml-2">messages left</span>
            </div>
            <Link to="/purchase" className="shrink-0">
              <Button className="bg-white hover:opacity-90 text-sm sm:text-base px-3 sm:px-4">Buy</Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Messages Area - min-h-0 needed for flex overflow */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden px-4 py-4 sm:px-6 sm:py-6 min-h-0 overscroll-contain">
        <div className="max-w-4xl mx-auto space-y-4">
          {messageBalance < 10 && messageBalance > 0 && (
            <div className="mb-4 p-3 sm:p-4 rounded-lg bg-white/10 border border-white/30 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-white flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-white font-medium">Low message balance</p>
                <p className="text-xs text-white/70 mt-1">
                  You have {messageBalance} messages remaining. Purchase more to continue chatting.
                </p>
              </div>
            </div>
          )}

          {messageBalance === 0 && (
            <div className="mb-4 p-6 rounded-lg bg-[#18181f] border border-white/[0.08] text-center">
              <div className="w-16 h-16 rounded-full bg-[#2a2a35] flex items-center justify-center mx-auto mb-4">
                <Sparkles className="w-8 h-8 text-white" />
              </div>
              <h3 className="font-['Poppins',sans-serif] text-lg font-semibold text-white mb-2">
                No Messages Left
              </h3>
              <p className="text-sm text-[#9595a5] mb-4">
                Purchase more messages to continue your conversation
              </p>
              <Link to="/purchase">
                <Button className="bg-white hover:opacity-90">Purchase Messages</Button>
              </Link>
            </div>
          )}

          {messages.map((message, i) => (
            <div
              key={i}
              className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[85%] sm:max-w-[70%] rounded-2xl px-3 py-2.5 sm:px-4 sm:py-3 ${
                  message.sender === 'user'
                    ? 'bg-white text-black'
                    : 'bg-[#2a2a35] text-white border border-white/[0.08]'
                }`}
              >
                <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">{message.content}</p>
              </div>
            </div>
          ))}

          {optimisticUserMessage && (
            <div className="flex justify-end">
              <div className="max-w-[85%] sm:max-w-[70%] rounded-2xl px-3 py-2.5 sm:px-4 sm:py-3 bg-white text-black">
                <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">{optimisticUserMessage}</p>
              </div>
            </div>
          )}

          {isSending && !pendingAiResponse && (
            <div className="flex justify-start">
              <div className="bg-[#2a2a35] text-white rounded-2xl px-3 py-2.5 sm:px-4 sm:py-3 border border-white/[0.08]">
                <div className="flex gap-1">
                  <div className="w-2 h-2 rounded-full bg-[#9595a5] animate-bounce" style={{ animationDelay: '0ms' }} />
                  <div className="w-2 h-2 rounded-full bg-[#9595a5] animate-bounce" style={{ animationDelay: '150ms' }} />
                  <div className="w-2 h-2 rounded-full bg-[#9595a5] animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            </div>
          )}

          {pendingAiResponse && (
            <div className="flex justify-start">
              <div className="max-w-[85%] sm:max-w-[70%] rounded-2xl px-3 py-2.5 sm:px-4 sm:py-3 bg-[#2a2a35] text-white border border-white/[0.08]">
                <p className="text-sm leading-relaxed whitespace-pre-wrap">
                  <TypewriterText
                    text={pendingAiResponse}
                    speed={40}
                    onComplete={handleTypewriterComplete}
                  />
                </p>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input Area - sticky on mobile */}
      <div className="sticky bottom-0 z-20 shrink-0 border-t border-white/[0.08] bg-[#18181f]/95 backdrop-blur-xl px-4 py-3 sm:px-6 sm:py-4 pb-[env(safe-area-inset-bottom)]">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-end gap-2 sm:gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
              <textarea
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
                placeholder={
                  messageBalance === 0
                    ? 'Purchase messages to continue...'
                    : 'Type your message...'
                }
                disabled={!chatId || messageBalance === 0 || isSending}
                className="w-full bg-[#2a2a35] text-white placeholder-[#9595a5] rounded-lg px-4 py-3 pr-12 sm:pr-16 resize-none border border-white/[0.08] focus:border-white/30 focus:outline-none focus:ring-2 focus:ring-white/20 disabled:opacity-50 disabled:cursor-not-allowed text-base"
                rows={1}
                style={{ minHeight: '44px', maxHeight: '120px' }}
              />
              <button
              onClick={handleSend}
              disabled={!inputText.trim() || !chatId || messageBalance === 0 || isSending}
              className="shrink-0 p-3 sm:p-4 rounded-lg bg-white text-black hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Send className="w-5 h-5" />
            </button>
            </div>
              <div className="flex items-center justify-between mt-1.5 sm:mt-2">
                <span className="text-xs text-[#9595a5]">
                  {characterCount}/{maxCharacters}
                </span>
                {messageBalance > 0 && (
                  <span className="hidden sm:inline text-xs text-[#9595a5]">Press Enter to send</span>
                )}
              </div>
            </div>
            
          </div>
        </div>
      </div>
    </div>
  );
}
